import { z } from 'zod';
import { CognitoSubSchema, TimestampSchema } from '../common.js';
import { Iso639_1Schema } from './agent.js';
/**
 * RAG knowledge configuration for an agent.
 *
 * V1 stores source text and metadata in DynamoDB so backoffice can administer
 * approved knowledge before the async vector-ingestion pipeline lands.
 */
export const AgentKnowledgeRetrievalModeSchema = z.enum(['disabled', 'enabled', 'strict']);
export const AgentKnowledgeLookupPolicySchema = z.enum(['factual', 'every-turn']);
export const AgentKnowledgeNoMatchPolicySchema = z.enum([
    'admit-unknown',
    'escalate',
    'allow-general',
]);
export const AgentKnowledgeLanguageFallbackSchema = z.enum(['none', 'default-language']);
export const AgentKnowledgeSourceTypeSchema = z.enum(['text', 'file', 'url']);
export const AgentKnowledgeSourceStatusSchema = z.enum([
    'draft',
    'processing',
    'ready',
    'failed',
    'archived',
]);
export const AgentKnowledgeConfigSchema = z.object({
    agentId: z.string().uuid(),
    retrievalMode: AgentKnowledgeRetrievalModeSchema.default('disabled'),
    lookupPolicy: AgentKnowledgeLookupPolicySchema.default('every-turn'),
    searchLimit: z.number().int().min(1).max(8).default(5),
    noMatchPolicy: AgentKnowledgeNoMatchPolicySchema.default('admit-unknown'),
    languageFallback: AgentKnowledgeLanguageFallbackSchema.default('none'),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    updatedBy: CognitoSubSchema,
});
export const AgentKnowledgeSourceSchema = z.object({
    sourceId: z.string().uuid(),
    agentId: z.string().uuid(),
    title: z.string().min(1).max(160),
    lang: Iso639_1Schema,
    sourceType: AgentKnowledgeSourceTypeSchema,
    status: AgentKnowledgeSourceStatusSchema,
    body: z.string().min(1).max(100_000).optional(),
    url: z.string().url().optional(),
    s3Key: z.string().min(1).optional(),
    mime: z.string().min(1).optional(),
    bytes: z.number().int().positive().optional(),
    checksum: z.string().min(1).optional(),
    /** Optional validity window for promotions, availability, and other time-sensitive facts. */
    validFrom: TimestampSchema.optional(),
    validUntil: TimestampSchema.optional(),
    chunkCount: z.number().int().nonnegative().default(0),
    errorMessage: z.string().max(1000).optional(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    createdBy: CognitoSubSchema,
});
export const AgentKnowledgeChunkSchema = z.object({
    chunkId: z.string().min(1).max(120),
    sourceId: z.string().uuid(),
    agentId: z.string().uuid(),
    lang: Iso639_1Schema,
    index: z.number().int().nonnegative(),
    text: z.string().min(1).max(4_000),
    tokenEstimate: z.number().int().positive(),
    embeddingModel: z.string().min(1).optional(),
    embeddingVector: z.array(z.number()).max(3072).optional(),
    createdAt: TimestampSchema,
});
//# sourceMappingURL=agentKnowledge.js.map